/**
 * Wishlist Manager
 * Stores wishlist in localStorage for all users.
 * Dispatches a custom event 'wishlist:updated' when the wishlist changes.
 */
class WishlistManager {
  static STORAGE_KEY = 'shopify-wishlist';

  static getWishlist() {
    try {
      const stored = localStorage.getItem(WishlistManager.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  static saveWishlist(handles) {
    try {
      localStorage.setItem(WishlistManager.STORAGE_KEY, JSON.stringify(handles));
      WishlistManager.dispatchUpdate();
    } catch (e) {
      /* localStorage might be full or disabled */
    }
  }

  static isWishlisted(handle) {
    return WishlistManager.getWishlist().includes(handle);
  }

  static toggle(handle) {
    let wishlist = WishlistManager.getWishlist();
    if (wishlist.includes(handle)) {
      wishlist = wishlist.filter(h => h !== handle);
    } else {
      wishlist.push(handle);
    }
    WishlistManager.saveWishlist(wishlist);
    return WishlistManager.isWishlisted(handle);
  }

  static add(handle) {
    const wishlist = WishlistManager.getWishlist();
    if (!wishlist.includes(handle)) {
      wishlist.push(handle);
      WishlistManager.saveWishlist(wishlist);
    }
  }

  static remove(handle) {
    let wishlist = WishlistManager.getWishlist();
    wishlist = wishlist.filter(h => h !== handle);
    WishlistManager.saveWishlist(wishlist);
  }

  static getCount() {
    return WishlistManager.getWishlist().length;
  }

  static dispatchUpdate() {
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { count: WishlistManager.getCount(), wishlist: WishlistManager.getWishlist() }
    }));
  }
}

/**
 * Wishlist Button Custom Element
 * Used on product cards and anywhere a wishlist toggle button is needed.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    this.button = this.querySelector('.card__wishlist-btn');
    if (!this.button || !this.handle) return;

    /* Set initial state */
    this.updateState();

    /* Click handler */
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      WishlistManager.toggle(this.handle);
      this.updateState();
    });

    /* Listen for wishlist updates from other buttons */
    this._updateHandler = () => this.updateState();
    document.addEventListener('wishlist:updated', this._updateHandler);
  }

  disconnectedCallback() {
    if (this._updateHandler) {
      document.removeEventListener('wishlist:updated', this._updateHandler);
    }
  }

  updateState() {
    if (!this.button || !this.handle) return;
    const isWishlisted = WishlistManager.isWishlisted(this.handle);
    this.button.classList.toggle('is-wishlisted', isWishlisted);
    this.button.setAttribute('aria-label', isWishlisted ? 'Remove from wishlist' : 'Add to wishlist');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * Wishlist Drawer Custom Element
 */
class WishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.wishlist-drawer__overlay');
    this.panel = this.querySelector('.wishlist-drawer__panel');
    this.grid = this.querySelector('#WishlistDrawerGrid');
    this.emptyState = this.querySelector('#WishlistDrawerEmpty');
    this.countEl = this.querySelector('.wishlist-drawer__count');
    this.closeBtn = this.querySelector('.wishlist-drawer__close');

    /* Open drawer when header icon is clicked */
    const openBtn = document.getElementById('wishlist-icon-bubble');
    if (openBtn) {
      openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    }

    /* Close drawer */
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    /* ESC key closes drawer */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });

    /* Listen for wishlist updates */
    document.addEventListener('wishlist:updated', () => {
      if (this.getAttribute('aria-hidden') === 'false') {
        this.renderDrawerItems();
      }
    });
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.renderDrawerItems();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `Rs. ${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  async renderDrawerItems() {
    const wishlist = WishlistManager.getWishlist();

    if (this.countEl) {
      this.countEl.textContent = wishlist.length;
    }

    if (wishlist.length === 0) {
      if (this.emptyState) this.emptyState.style.display = 'block';
      if (this.grid) this.grid.style.display = 'none';
      return;
    }

    if (this.emptyState) this.emptyState.style.display = 'none';
    if (this.grid) {
      this.grid.style.display = '';
      /* Show loading spinner immediately so drawer isn't blank */
      if (!this.grid.children.length) {
        this.grid.innerHTML = '<li class="wishlist-drawer__loading" style="text-align:center;padding:3rem;grid-column:1/-1;"><svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="animation:spin .8s linear infinite"><style>@keyframes spin{to{transform:rotate(360deg)}}</style><circle cx="12" cy="12" r="10" fill="none" stroke="#ccc" stroke-width="2"/><path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round"/></svg></li>';
      }
    }

    const products = await this.fetchProducts(wishlist);
    this.renderGrid(products);
  }

  async fetchProducts(handles) {
    const rootUrl = window.Shopify?.routes?.root || '/';

    /* Initialize cache */
    if (!this._cache) this._cache = {};

    /* Fetch all products in parallel */
    const results = await Promise.all(
      handles.map(async (handle) => {
        /* Return cached version if available */
        if (this._cache[handle]) return this._cache[handle];
        try {
          const response = await fetch(`${rootUrl}products/${handle}.js`);
          if (response.ok) {
            const product = await response.json();
            this._cache[handle] = product;
            return product;
          }
        } catch (e) { /* skip */ }
        return null;
      })
    );

    return results.filter(Boolean);
  }

  renderGrid(products) {
    if (!this.grid) return;

    this.grid.innerHTML = products.map(product => {
      const image = product.featured_image || product.images?.[0] || '';
      const url = `/products/${product.handle}`;
      const price = product.price;
      const comparePrice = product.compare_at_price;
      const onSale = comparePrice && comparePrice > price;

      let priceHtml = '';
      if (onSale) {
        priceHtml = `<s>${this.formatMoney(comparePrice)}</s> <span class="sale-price">${this.formatMoney(price)}</span>`;
      } else {
        priceHtml = this.formatMoney(price);
      }

      return `
        <li class="wishlist-drawer__item">
          <a href="${url}" class="wishlist-drawer__item-image" onclick="document.getElementById('WishlistDrawer').querySelector('wishlist-drawer, .wishlist-drawer').close ? document.getElementById('WishlistDrawer').close() : null">
            ${image ? `<img src="${image}" alt="${product.title}" loading="lazy" width="90" height="90">` : ''}
          </a>
          <div class="wishlist-drawer__item-info">
            <h3 class="wishlist-drawer__item-title">
              <a href="${url}">${product.title}</a>
            </h3>
            <div class="wishlist-drawer__item-price">${priceHtml}</div>
            <button type="button" class="wishlist-drawer__item-remove" data-handle="${product.handle}">Remove</button>
          </div>
        </li>
      `;
    }).join('');

    /* Attach remove handlers */
    this.grid.querySelectorAll('.wishlist-drawer__item-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.handle;
        /* Remove from cache too */
        if (this._cache) delete this._cache[handle];
        WishlistManager.remove(handle);
      });
    });
  }
}

if (!customElements.get('wishlist-drawer')) {
  customElements.define('wishlist-drawer', WishlistDrawer);
}

/**
 * Wishlist Count Updater
 * Updates all wishlist count bubbles in the header whenever the wishlist changes.
 */
function updateWishlistCounts() {
  const count = WishlistManager.getCount();
  document.querySelectorAll('.wishlist-count-bubble').forEach(bubble => {
    bubble.setAttribute('data-count', count);
    const span = bubble.querySelector('span:not(.visually-hidden)');
    if (span) span.textContent = count;
    const srSpan = bubble.querySelector('.visually-hidden');
    if (srSpan) srSpan.textContent = `${count} items in wishlist`;
  });
}

/* Listen for wishlist updates */
document.addEventListener('wishlist:updated', updateWishlistCounts);

/* Initialize counts on page load */
document.addEventListener('DOMContentLoaded', updateWishlistCounts);
/* Also run immediately for cases where DOMContentLoaded already fired */
updateWishlistCounts();
